import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { businessDateIn } from '@optiflow/shared';
import {
  EXPENSE_PAYMENT_METHODS,
  PERMISSIONS,
  expenseNeedsReference,
  type AuthenticatedBranch,
  type ExpensePaymentMethod,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import {
  useExpenseCategoriesQuery,
  useExpenseSuppliersQuery,
  usePostExpenseMutation,
} from '../api/expenses.api';
import { InactiveBadge } from '../components/ExpenseBadges';
import { FROM_RECORD_EXPENSE } from './categoriesReturn';

/**
 * Recording an operating cost that has already been paid.
 *
 * A page rather than a dialog: a category, an amount, how it was paid and who got it are real
 * decisions, and the review below is worth reading before the money is recorded as gone.
 *
 * There is no draft and no approval, because the money has already left. Nothing on this page
 * mentions a balance, a due date or settling anything later — an expense that could be "paid
 * later" would be a supplier bill, which is a different domain entirely.
 *
 * The date defaults to today **in the branch's own timezone**, taken from the branch the request
 * will act in. The browser's clock decides nothing: a shop in Cairo closing at midnight must not
 * book Tuesday's electricity on Monday because the laptop is set to London.
 */

/**
 * The branch this page opens on.
 *
 * One reachable branch is resolved, because there is nothing to choose. More than one, and the
 * branch already being worked in is a reasonable opening guess. With neither — several branches and
 * none of them active — the field opens **empty** and Post stays disabled until someone says which
 * shop the money left. Falling back to the first branch in the list would book a real cost against
 * a real shop for no better reason than where it happened to sort.
 */
function openingBranchId(
  branches: readonly AuthenticatedBranch[],
  activeBranch: AuthenticatedBranch | null,
): string {
  if (branches.length === 1) return branches[0]?.id ?? '';
  if (activeBranch && branches.some((branch) => branch.id === activeBranch.id)) {
    return activeBranch.id;
  }
  return '';
}
export function RecordExpensePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches, activeBranch } = useBranch();
  const { format, parse } = useCurrency();
  const { can } = usePermissions();

  const [branchId, setBranchId] = useState(() => openingBranchId(branches, activeBranch));
  const [categoryId, setCategoryId] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [method, setMethod] = useState<ExpensePaymentMethod>('CASH');
  const [externalReference, setExternalReference] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [payee, setPayee] = useState('');
  const [notes, setNotes] = useState('');
  const [expenseDate, setExpenseDate] = useState('');
  const [dateTouched, setDateTouched] = useState(false);

  const branch = branches.find((candidate) => candidate.id === branchId);
  // Today where the branch is, not where the browser is.
  const businessToday = useMemo(() => (branch ? businessDateIn(branch.timezone) : ''), [branch]);

  useEffect(() => {
    if (!dateTouched && businessToday) setExpenseDate(businessToday);
  }, [businessToday, dateTouched]);

  // Only what may be used for a new expense. Retired categories stay readable on old documents.
  const categories = useExpenseCategoriesQuery({ active: true, pageSize: 100 });
  // Nothing to choose from: say so rather than offer an empty picker. Only once the list has loaded, so a slow request never flashes it.
  const noCategories = categories.isSuccess && categories.data.items.length === 0;
  const canManageCategories = can(PERMISSIONS.EXPENSES_CATEGORIES_MANAGE);
  const deferredSupplierSearch = useDeferredValue(supplierSearch);
  const suppliers = useExpenseSuppliersQuery({ search: deferredSupplierSearch, pageSize: 50 });
  const post = usePostExpenseMutation();

  const amountMinor = parse(amountInput) ?? 0;
  const needsReference = expenseNeedsReference(method);
  const missingReference = needsReference && externalReference.trim().length === 0;
  const inFuture = Boolean(expenseDate && businessToday && expenseDate > businessToday);
  const chosenSupplier = (suppliers.data?.items ?? []).find(
    (supplier) => supplier.id === supplierId,
  );
  const chosenCategory = (categories.data?.items ?? []).find(
    (category) => category.id === categoryId,
  );

  // The category went out of use while this page was open; the server said so and nothing was saved.
  const categoryRefused = apiErrorCode(post.error) === 'EXPENSE_CATEGORY_INACTIVE';

  const canPost =
    Boolean(branchId) &&
    Boolean(categoryId) &&
    Boolean(expenseDate) &&
    amountMinor > 0 &&
    !inFuture &&
    !missingReference &&
    !post.isPending;

  const submit = () => {
    if (!canPost) return;
    post.mutate(
      {
        branchId,
        categoryId,
        expenseDate,
        amountMinor,
        method,
        ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
        ...(supplierId ? { supplierId } : {}),
        ...(payee.trim() ? { payee: payee.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      {
        onSuccess: (expense) => void navigate(`/finance/expenses/${expense.id}`),
        // What may still be chosen has moved on; the entered figures stay for review.
        onError: () => void categories.refetch(),
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('expenses.record')}
        subtitle={t('expenses.recordSubtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void navigate('/finance/expenses')}
              disabled={post.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} isLoading={post.isPending} disabled={!canPost}>
              {t('expenses.post')}
            </Button>
          </div>
        }
      />

      {post.isError ? (
        <>
          <Alert tone="error">{apiErrorMessage(post.error) ?? t('expenses.postError')}</Alert>
          <Alert tone="info">
            {categoryRefused ? t('expenses.categoryWentInactive') : t('expenses.inputKept')}
          </Alert>
        </>
      ) : null}
      {inFuture ? (
        <Alert tone="warning">{t('expenses.dateInFuture', { date: businessToday })}</Alert>
      ) : null}

      <Card>
        <CardHeader title={t('expenses.whatSection')} subtitle={t('expenses.whatHint')} />
        <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {branches.length > 1 ? (
            <FormField label={t('expenses.fields.branch')} required hint={t('expenses.branchHint')}>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  options={[
                    { value: '', label: t('expenses.chooseBranch') },
                    ...branches.map((candidate) => ({
                      value: candidate.id,
                      label: candidate.name,
                    })),
                  ]}
                  value={branchId}
                  onChange={(event) => setBranchId(event.target.value)}
                />
              )}
            </FormField>
          ) : (
            <div className="space-y-0.5">
              <p className="text-muted text-xs">{t('expenses.fields.branch')}</p>
              <p className="text-ink text-sm" dir="auto">
                {branch?.name ?? '—'}
              </p>
              <p className="text-muted text-xs">{t('expenses.onlyBranch')}</p>
            </div>
          )}

          <Input
            type="date"
            label={t('expenses.fields.expenseDate')}
            required
            dir="ltr"
            value={expenseDate}
            max={businessToday || undefined}
            hint={t('expenses.dateHint')}
            onChange={(event) => {
              setDateTouched(true);
              setExpenseDate(event.target.value);
            }}
          />

          {noCategories ? (
            <div className="sm:col-span-2 lg:col-span-4">
              <Alert tone="warning">
                <div className="space-y-3">
                  <p className="font-medium">{t('expenses.noCategories.title')}</p>
                  {canManageCategories ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        void navigate('/finance/expenses/categories', {
                          state: FROM_RECORD_EXPENSE,
                        })
                      }
                    >
                      {t('expenses.noCategories.create')}
                    </Button>
                  ) : (
                    <p>{t('expenses.noCategories.askManager')}</p>
                  )}
                </div>
              </Alert>
            </div>
          ) : (
            <FormField
              label={t('expenses.fields.category')}
              required
              hint={t('expenses.categoryIsType')}
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  options={[
                    { value: '', label: t('expenses.chooseCategory') },
                    ...(categories.data?.items ?? []).map((category) => ({
                      value: category.id,
                      label: category.name,
                    })),
                  ]}
                  value={categoryId}
                  onChange={(event) => setCategoryId(event.target.value)}
                />
              )}
            </FormField>
          )}

          <Input
            label={t('expenses.fields.amount')}
            required
            dir="ltr"
            inputMode="decimal"
            value={amountInput}
            hint={t('expenses.amountHint')}
            onChange={(event) => setAmountInput(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('expenses.howSection')} subtitle={t('expenses.howHint')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('expenses.fields.method')} required>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={EXPENSE_PAYMENT_METHODS.map((value) => ({
                  value,
                  label: t(`expenses.methods.${value}`),
                }))}
                value={method}
                onChange={(event) => setMethod(event.target.value as ExpensePaymentMethod)}
              />
            )}
          </FormField>

          <Input
            label={t(`expenses.referenceLabels.${method}`)}
            required={needsReference}
            dir="ltr"
            value={externalReference}
            hint={
              needsReference ? t('expenses.referenceRequired') : t('expenses.referenceOptional')
            }
            onChange={(event) => setExternalReference(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('expenses.whoSection')} subtitle={t('expenses.whoHint')} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Input
            type="search"
            label={t('expenses.findSupplier')}
            value={supplierSearch}
            hint={t('expenses.findSupplierHint')}
            onChange={(event) => setSupplierSearch(event.target.value)}
          />

          <FormField label={t('expenses.fields.supplier')} hint={t('expenses.supplierHint')}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={[
                  { value: '', label: t('expenses.noSupplier') },
                  ...(suppliers.data?.items ?? []).map((supplier) => ({
                    value: supplier.id,
                    label: supplier.isActive
                      ? supplier.name
                      : t('expenses.supplierInactiveOption', { name: supplier.name }),
                  })),
                ]}
                value={supplierId}
                onChange={(event) => setSupplierId(event.target.value)}
              />
            )}
          </FormField>

          {chosenSupplier && !chosenSupplier.isActive ? (
            <div className="sm:col-span-2">
              <Alert tone="info">
                <span className="flex flex-wrap items-center gap-2">
                  <InactiveBadge />
                  <span>{t('expenses.inactiveSupplierAllowed')}</span>
                </span>
              </Alert>
            </div>
          ) : null}

          <Input
            label={t('expenses.fields.payee')}
            value={payee}
            hint={t('expenses.payeeHint')}
            onChange={(event) => setPayee(event.target.value)}
          />

          <Input
            label={t('expenses.fields.notes')}
            value={notes}
            hint={t('expenses.notesHint')}
            onChange={(event) => setNotes(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('expenses.reviewSection')} />
        <CardBody className="space-y-2 text-sm">
          {(
            [
              [t('expenses.fields.branch'), branch?.name ?? '—', false],
              [t('expenses.fields.expenseDate'), expenseDate || '—', true],
              [t('expenses.fields.category'), chosenCategory?.name ?? '—', false],
              [t('expenses.fields.amount'), format(amountMinor), true],
              [t('expenses.fields.method'), t(`expenses.methods.${method}`), false],
              ...(needsReference || externalReference.trim()
                ? ([
                    [t('expenses.fields.externalReference'), externalReference || '—', true],
                  ] as const)
                : []),
              ...(chosenSupplier
                ? ([[t('expenses.fields.supplier'), chosenSupplier.name, false]] as const)
                : []),
              ...(payee.trim() ? ([[t('expenses.fields.payee'), payee, false]] as const) : []),
              ...(notes.trim() ? ([[t('expenses.fields.notes'), notes, false]] as const) : []),
            ] as [string, string, boolean][]
          ).map(([label, value, ltr]) => (
            <div key={label} className="flex flex-wrap justify-between gap-3">
              <span className="text-muted">{label}</span>
              <span className="text-ink" dir={ltr ? 'ltr' : 'auto'}>
                {value}
              </span>
            </div>
          ))}

          <p className="text-muted border-line border-t pt-2 text-xs">
            {t('expenses.postingExplains')}
          </p>
          <p className="text-muted text-xs">{t('expenses.noPayableCreated')}</p>
        </CardBody>
      </Card>
    </div>
  );
}
